import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  styled,
  IconButton,
  InputAdornment,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RootState } from 'src/redux-store/appStore';
import { useSelector, useDispatch } from 'react-redux';
import { httpRequest } from 'src/services/httpRequest';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { setUserDetails, clearUserDetails } from 'src/redux-store/slices/userSlice';
import { logout as setLogout } from 'src/redux-store/slices/logoutSlice';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3)
}));

const validationSchema = yup.object({
  currentPassword: yup
    .string()
    .required('Current password cannot be blank.')
    .min(8, 'Use at least 8 characters.')
    .matches(/[0-9a-zA-Z_@./#&]/, 'Password contains invalid characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .matches(
      /[^a-zA-Z0-9]/,
      'Password must contain at least one special character.'
    ),

  newPassword: yup
    .string()
    .required('New password cannot be blank.')
    .min(8, 'Use at least 8 characters.')
    .matches(/[0-9a-zA-Z_@./#&]/, 'Password contains invalid characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .matches(
      /[^a-zA-Z0-9]/,
      'Password must contain at least one special character.'
    )
    .notOneOf(
      [yup.ref('currentPassword')],
      'New password must be different from current password.'
    ),

  confirmPassword: yup
    .string()
    .required('Re-enter password cannot be blank.')
    .oneOf([yup.ref('newPassword')], 'Password is not matching.')
});

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state: RootState) => state.user);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const fromAdminReset =
    (userDetails as any)?.IsPasswordUpdatedByAdmin === true;

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async () => {
      setIsLoading(true);
      setSubmitError(null);

      const payload = {
        oldPassword: formik.values.currentPassword,
        newPassword: formik.values.newPassword
      };

      try {
        const response = await httpRequest(
          'POST',
          '/auth/update-existing-password',
          payload,
          {}
        );
        if (
          response.type === 'NETWORK_ERROR' ||
          response.status !== 'success'
        ) {
          throw new Error(response.message || 'Failed to create bid');
        }

        setSubmitSuccess(true);
        formik.resetForm();

        // Clear admin-reset flag so user can access other pages
        dispatch(
          setUserDetails({
            ...userDetails,
            IsPasswordUpdatedByAdmin: false
          })
        );

        if (fromAdminReset) {
          setShowLoginDialog(true);
        }
      } catch (error) {
        setSubmitError(error.message || 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    }
  });

  const handleClear = () => {
    formik.resetForm();
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 3,
          ml: 2,
          gap: 2
        }}
      >
        {!fromAdminReset && !showLoginDialog && (
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            <ArrowBack />
          </IconButton>
        )}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 140,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Change Password
        </Typography>
      </Box>{' '}
      <Container maxWidth="md">
        <StyledPaper>
          <Typography variant="h4" gutterBottom>
            Change Password
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {submitError}
            </Alert>
          )}

          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password changed successfully
            </Alert>
          )}

          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  * Indicates required field
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography>
                  Email address: {userDetails?.EmailID || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="currentPassword"
                  name="currentPassword"
                  label="Current Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  required
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.currentPassword &&
                    Boolean(formik.errors.currentPassword)
                  }
                  helperText={
                    formik.touched.currentPassword &&
                    formik.errors.currentPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="newPassword"
                  name="newPassword"
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.newPassword &&
                    Boolean(formik.errors.newPassword)
                  }
                  helperText={
                    formik.touched.newPassword && formik.errors.newPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Re-enter Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Submit'}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleClear}
                    disabled={isLoading}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Please note passwords are case-sensitive. This means that if
                  your password has lowercase or UPPERCASE letters, they must be
                  entered in the appropriate case to be able to log in
                  successfully.
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {isLoading && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                Change password is in process. Please login again once it is
                done.
              </Typography>
              <CircularProgress />
            </Box>
          )}
        </StyledPaper>
      </Container>

      <Dialog
        open={showLoginDialog}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <DialogTitle>Login Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Your password has been updated successfully. For security reasons,
            you need to login again using your new password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const segments = location.pathname.split('/');
              const prefix = segments.slice(0, 4).join('/');
              (async () => {
                try {
                  setIsLoggingOut(true);
                  await httpRequest('POST', '/auth/logout', {}, {});
                } catch (e) {
                  // ignore logout API error, still clear client state
                } finally {
                  dispatch(clearUserDetails());
                  dispatch(setLogout());
                  sessionStorage.removeItem('redirectPath');
                  navigate(`${prefix}/login`, { replace: true });
                  setIsLoggingOut(false);
                }
              })();
            }}
            variant="contained"
            color="primary"
            disabled={isLoggingOut}
          >
            {isLoggingOut ? <CircularProgress size={20} /> : 'Go to Login'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangePassword;
